import type { PageSideFilters } from './useProductsPage';

export type FilterPill = {
  key: string;
  value: unknown;
  index?: number;
};

type UseFilterPillsReturn = {
  pills: FilterPill[];
  showClearAll: boolean;
  handleClearAll: () => void;
  createBikeDetailsRemoveHandler: (index: number) => () => void;
};

export const useFilterPills = (
  filters: PageSideFilters,
  handleFilterRemove: (filter: { key: string; value: unknown }) => void,
  clearAllFilters: () => void,
  onClearBikeFilters?: () => void
): UseFilterPillsReturn => {
  const pills: FilterPill[] = [];

  if (
    filters.bikeYears?.length &&
    filters.bikeMakes?.length &&
    filters.bikeModels?.length
  ) {
    for (let i = 0; i < (filters.bikeYears?.length || 0); i++) {
      if (
        filters.bikeYears &&
        filters.bikeMakes &&
        filters.bikeModels &&
        filters.bikeMakes[i] !== undefined &&
        filters.bikeModels[i] !== undefined
      ) {
        const combinedValue = `${filters.bikeYears[i]} ${filters.bikeMakes[i]} ${filters.bikeModels[i]}`;
        pills.push({
          key: 'bikeDetails',
          value: [combinedValue],
          index: i,
        });
      }
    }
  } else {
    Object.entries(filters ?? {}).forEach(([key, value]) => {
      if (!['bikeYears', 'bikeMakes', 'bikeModels'].includes(key)) {
        // Special handling for size filters to create readable pill names
        if (key === 'letterSizes' && Array.isArray(value) && value.length > 0) {
          value.forEach((size) => {
            pills.push({
              key: 'letterSizes',
              value: size,
            });
          });
        } else if (
          key === 'numberSizes' &&
          Array.isArray(value) &&
          value.length > 0
        ) {
          value.forEach((size) => {
            pills.push({
              key: 'numberSizes',
              value: size,
            });
          });
        } else if (key !== 'letterSizes' && key !== 'numberSizes') {
          // Handle all other non-size, non-bike filters normally
          pills.push({ key, value });
        }
      }
    });
  }

  const showClearAll =
    pills.filter((p) =>
      Array.isArray(p.value)
        ? p.value.length > 0
        : p.value && typeof p.value === 'object'
        ? Object.values(p.value).filter((v) => !!v).length > 0
        : !!p.value
    ).length > 1;

  const handleClearAll = () => {
    filters.bikeYears = [];
    filters.bikeMakes = [];
    filters.bikeModels = [];
    onClearBikeFilters?.();
    clearAllFilters();
  };

  const createBikeDetailsRemoveHandler = (index: number) => () => {
    if (filters.bikeYears && filters.bikeMakes && filters.bikeModels) {
      const newYears = [...filters.bikeYears];
      const newMakes = [...filters.bikeMakes];
      const newModels = [...filters.bikeModels];
      newYears.splice(index, 1);
      newMakes.splice(index, 1);
      newModels.splice(index, 1);
      filters.bikeYears = newYears;
      filters.bikeMakes = newMakes;
      filters.bikeModels = newModels;

      handleFilterRemove({
        key: 'bikeYears',
        value: newYears,
      });
      handleFilterRemove({
        key: 'bikeMakes',
        value: newMakes,
      });
      handleFilterRemove({
        key: 'bikeModels',
        value: newModels,
      });

      onClearBikeFilters?.();
    }
  };

  return {
    pills,
    showClearAll,
    handleClearAll,
    createBikeDetailsRemoveHandler,
  };
};
